import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostBinding, Input } from '@angular/core';
import { waitForDrawnFrameOrTimeoutAsync } from 'services/browser-utils';

@Component({
  selector: 'cs-tab-content',
  templateUrl: 'cs-tab-content.html',
  host: { 'class': 'tab-content' },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsTabContent {
  // value for automatic mode (container manages animations based on selection)
  @Input() value: any;

  // #4410 opt-in: rebuild this panel's iOS compositor layer whenever it
  // arrives (see _rebuildLayerWhileHiddenAsync). Only safe on a panel whose
  // EVERY arrival replaces its content — the rebuild's side effect is that
  // display:none resets descendant scroll positions to the top. The
  // Training and Nutrition A/B day panels qualify: their only selection
  // change is a day switch, so the reset is visible but harmless (on iOS the
  // arriving day now starts at the top instead of keeping a stale offset from
  // two switches ago). Home's daylog panels do NOT qualify even though they
  // use the same A/B recycling: returning from the Progress tab re-arrives
  // the same daylog panel with its content unchanged, and the user's scroll
  // position must survive that — so Home deliberately leaves this off and
  // its day switcher stays exposed to the stale-layer blank (recorded in
  // design-docs/deferred-findings/).
  @Input() rebuildLayerOnEnter: boolean = false;

  // internal animation state managed by container
  private _active: boolean = false;
  private _entering: boolean = false;
  private _enteringBack: boolean = false;
  private _leaving: boolean = false;
  private _leavingBack: boolean = false;

  constructor(private cdr: ChangeDetectorRef, private elementRef: ElementRef) {}

  // #4410: on iOS, a reused panel that was hidden and is shown again can come
  // back with a stale compositor layer — the panel then measures correctly and
  // answers hit tests, but paints nothing (the "blank day"). Nothing in CSS or
  // JS marks such a layer dirty, so the only reliable repair is to make iOS
  // throw the layer away: take the element out of the render tree with
  // display:none, hold that until a frame has GENUINELY committed with it
  // gone, then restore it. Full reasoning and the measured dead ends:
  // design-docs/navigation.md, "iOS stale-layer rebuild on tab switch".
  // The container calls this while the panel is parked off-screen in its
  // entering state, so the toggle is never visible.
  async _rebuildLayerWhileHiddenAsync(): Promise<void> {
    const el = this.elementRef.nativeElement as HTMLElement;
    el.style.display = 'none';
    try {
      // two waits: the hidden state must survive into a committed frame to
      // reach the compositor process — a same-frame restore does nothing
      await waitForDrawnFrameOrTimeoutAsync();
      await waitForDrawnFrameOrTimeoutAsync();
    } finally {
      // restore unconditionally — the panel must never stay out of the render tree
      el.style.display = '';
    }
    // one more committed frame before the caller activates the panel — a CSS
    // transition started in the same frame an element leaves display:none
    // snaps instead of animating
    await waitForDrawnFrameOrTimeoutAsync();
  }

  @HostBinding('class.active')
  get isActive(): boolean {
    return this._active;
  }

  @HostBinding('class.entering')
  get isEntering(): boolean {
    return this._entering;
  }

  @HostBinding('class.entering-back')
  get isEnteringBack(): boolean {
    return this._enteringBack;
  }

  @HostBinding('class.leaving')
  get isLeaving(): boolean {
    return this._leaving;
  }

  @HostBinding('class.leaving-back')
  get isLeavingBack(): boolean {
    return this._leavingBack;
  }

  /** Called by container to update animation state */
  _setAnimationState(state: { active: boolean; entering: boolean; enteringBack: boolean; leaving: boolean; leavingBack: boolean }) {
    this._active = state.active;
    this._entering = state.entering;
    this._enteringBack = state.enteringBack;
    this._leaving = state.leaving;
    this._leavingBack = state.leavingBack;
    this.cdr.markForCheck();
  }
}
